import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, motion, radii, spacing, typography } from '../../theme-v2';

export type CTAButtonVariant = 'outlined' | 'primary';

export interface CTAButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  /** Visible label. */
  children: ReactNode;
  /** Optional leading icon (typically a phosphor icon at size 14). */
  icon?: ReactNode;
  /**
   * `outlined` (default) — white pill with gray border + dark text
   * (Contact sender / Book a meeting / Save document). `primary` —
   * solid teal pill with white text (Share).
   */
  variant?: CTAButtonVariant;
  /**
   * Overrides the `primary` color
   */
  accentColor?: string;
}

/**
 * CTAButton — pill-shaped action button used in the experience page
 * top bar (Contact sender, Book a meeting, Save document, Share) and
 * other call-to-action contexts.
 *
 * Consumes theme-v2 tokens directly so it renders correctly regardless
 * of the surrounding AntD `ConfigProvider`. Two variants: `outlined`
 * (default, neutral) and `primary` (accent-filled).
 */
export const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      children,
      icon,
      variant = 'outlined',
      accentColor,
      type = 'button',
      ...rest
    },
    ref,
  ) => (
    <StyledButton
      ref={ref}
      type={type}
      $variant={variant}
      $accent={accentColor}
      {...rest}
    >
      {icon && <StyledIconSlot>{icon}</StyledIconSlot>}
      <span>{children}</span>
    </StyledButton>
  ),
);

CTAButton.displayName = 'CTAButton';

const outlined = css`
  background: transparent;
  border: 1px solid ${colors.border.subtle};
  color: ${colors.text.primary};

  &:hover:not(:disabled) {
    border-color: ${colors.accent.base};
    color: ${colors.accent.base};
  }
`;

const primary = css`
  background: ${colors.accent.base};
  border: 1px solid ${colors.accent.base};
  color: ${colors.text.onAccent};

  &:hover:not(:disabled) {
    background: ${colors.accent.hover};
    border-color: ${colors.accent.hover};
  }

  &:active:not(:disabled) {
    background: ${colors.accent.active};
    border-color: ${colors.accent.active};
  }
`;

// Same shape as `primary`, but filled with a caller-supplied color. Hover /
// active shading is done with brightness so any brand color works without a
// color-manipulation dependency.
const primaryBranded = (accent: string) => css`
  background: ${accent};
  border: 1px solid ${accent};
  color: ${colors.text.onAccent};

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:active:not(:disabled) {
    filter: brightness(0.92);
  }
`;

const variantStyle = ($variant: CTAButtonVariant, $accent?: string) => {
  if ($variant !== 'primary') return outlined;
  return $accent ? primaryBranded($accent) : primary;
};

// Control height / horizontal padding. Internal to CTAButton (consistent
// with how libs/ui Button / IconButton store their size configs locally
// rather than as exported theme tokens). Matches the updated CTA spec
// (38px height, 20px horizontal padding, 4px icon/label gap) — 20px has
// no exact match in the spacing scale (base=16, lg=24), hence the literal.
const CTA_HEIGHT = 38;
const CTA_PADDING_H = 20;

const StyledButton = styled.button<{
  $variant: CTAButtonVariant;
  $accent?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs}px;
  height: ${CTA_HEIGHT}px;
  padding: 0 ${CTA_PADDING_H}px;
  border-radius: ${radii.full}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.lineHeight.tight};
  white-space: nowrap;
  cursor: pointer;
  transition:
    background ${motion.duration.fast} ${motion.easing.standard},
    border-color ${motion.duration.fast} ${motion.easing.standard},
    color ${motion.duration.fast} ${motion.easing.standard};

  ${({ $variant, $accent }) => variantStyle($variant, $accent)}

  &:focus-visible {
    outline: 2px solid ${colors.accent.base};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const StyledIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
  }
`;
